import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

/**
 * Dados regulatórios ANVISA/SNGPC de um produto (relação 1:1).
 * Endpoints: GET/PUT/DELETE /api/produtos/{id}/medicamento.
 * Enums seguem o XSD do SNGPC (ver SngpcEnums no backend).
 */
export interface ProdutoMedicamentoData {
  id: number;
  produtoId: number;
  subProdutoId: number;
  // Identificação ANVISA
  registroMs?: string | null;
  codigoDcb?: string | null;
  principioAtivo?: string | null;
  concentracao?: string | null;
  formaFarmaceutica?: string | null;
  // Classificação SNGPC
  classeTerapeutica?: number | null; // null=não-controlado, 1=Antimicrobiano, 2=Controle Especial
  listaPortaria344?: string | null;
  controlado: boolean;
  antimicrobiano: boolean;
  usoContinuo: boolean;
  // Dispensação
  tipoUso: number; // 1=Humano, 2=Veterinário
  exigeReceita: boolean;
  tipoReceituarioPadrao?: number | null; // 1..5
  unidadeMedidaSngpc: number; // 1=Caixas, 2=Frascos
  // Classificação comercial
  generico: boolean;
  similar: boolean;
  referencia: boolean;
  // Fabricante
  cnpjFabricante?: string | null;
  nomeFabricante?: string | null;
}

export interface SalvarProdutoMedicamentoRequest {
  registroMs?: string | null;
  codigoDcb?: string | null;
  principioAtivo?: string | null;
  concentracao?: string | null;
  formaFarmaceutica?: string | null;
  classeTerapeutica?: number | null;
  listaPortaria344?: string | null;
  usoContinuo: boolean;
  tipoUso: number;
  exigeReceita: boolean;
  tipoReceituarioPadrao?: number | null;
  unidadeMedidaSngpc: number;
  generico: boolean;
  similar: boolean;
  referencia: boolean;
  cnpjFabricante?: string | null;
  nomeFabricante?: string | null;
}

export const useProdutoMedicamento = (produtoId: string | undefined) => {
  const qc = useQueryClient();
  const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-medicamento", numId];
  const base = `/api/produtos/${numId}/medicamento`;

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const resp = await apiClient.get<ProdutoMedicamentoData>(base);
      // 204 No Content → produto sem dados SNGPC cadastrados.
      return resp.status === 204 ? null : resp.data;
    },
    enabled: !!numId,
  });

  const save = useMutation({
    mutationFn: async (body: SalvarProdutoMedicamentoRequest) =>
      (await apiClient.put<ProdutoMedicamentoData>(base, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Dados SNGPC salvos!"); },
    onError: (e: Error) => toast.error(`Erro ao salvar dados SNGPC: ${e.message}`),
  });

  return {
    medicamento: query.data ?? null,
    isLoading: query.isLoading,
    salvarMedicamento: save.mutateAsync,
    salvando: save.isPending,
  };
};
